import { memo, type ReactNode, useMemo } from 'react';
import { Provider } from 'react-redux';

import { type ShortUrlResponse } from '@air/api/types';

import { makeStore } from '~/store';
import { setCentralizedPublicBoardAction } from '~/store/centralizedBoard/actions';
import {
  setCardSizeAction,
  setCurrentViewTypeAction,
  setSavedConfigurableViewsAction,
  setThumbnailPreferenceAction,
} from '~/store/configViews/actions';
import { type PublicBoardShareData } from '~/swr-hooks/shortUrl/utils';

export type PublicBoardReduxProviderProps = {
  children: ReactNode;
  initialBoard: PublicBoardShareData['board'];
  presentationSettings?: ShortUrlResponse['presentationSettings'];
};

export const PublicBoardReduxProvider = memo(
  ({ children, initialBoard, presentationSettings }: PublicBoardReduxProviderProps) => {
    const { store } = useMemo(() => {
      const { store } = makeStore({
        centralizedBoard: initialBoard,
      });

      store.dispatch(
        setCentralizedPublicBoardAction({
          board: initialBoard,
        }),
      );

      const defaultView = presentationSettings?.defaultView;

      if (initialBoard.views) {
        store.dispatch(
          setSavedConfigurableViewsAction({
            savedConfigurableViews: initialBoard.views,
            defaultMetadataFieldsVisible: defaultView?.metadataFieldsVisible,
          }),
        );
      }

      if (defaultView?.viewType) {
        store.dispatch(setCurrentViewTypeAction({ viewTypeName: defaultView.viewType.name }));
      }

      if (defaultView?.cardSize) {
        store.dispatch(setCardSizeAction({ cardSize: defaultView.cardSize }));
      }

      if (defaultView?.thumbnailStyle) {
        store.dispatch(setThumbnailPreferenceAction({ thumbnailPreference: defaultView.thumbnailStyle }));
      }

      return { store };
    }, [initialBoard, presentationSettings]);

    return <Provider store={store}>{children}</Provider>;
  },
);

PublicBoardReduxProvider.displayName = 'PublicBoardReduxProvider';
